import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Link from '../Link/Link';
import { IModalProps } from '../../../interfaces/interfaces';
import keyboardTriggerBasic from '../../../utils/keyboard-trigger';
import InlineEditable from '../InlineEditable/InlineEditable';

function ContactFormFooter(props: {privacyLinkHref: string, privacyLinkText: string}) {
  if(!props.privacyLinkHref || !props.privacyLinkText) {
    return (<></>);
  }
  return(
    <div className="modal-footer">
      <p className="tx_1">
        <Trans i18nKey="cockpit.contactModule.editModal.privacyText">Es gelten die</Trans>{' '}
        <Link
          linkClass="link link--underline"
          linkHref={props.privacyLinkHref}
          linkText={props.privacyLinkText}
        />
        .
      </p>
    </div>)
}

function ContactFormModal(props: IModalProps): JSX.Element {
  const { onConfirm, onAbort, abortText, confirmText, data } = props;
  const modalRef = useRef<HTMLAnchorElement>(null);
  const { t } = useTranslation();

  // EDIATBLE STATES
  const [updatedName, updateName] = useState(data.contactName);
  const [updatedDescription, updateDescription] = useState(data.contactDescription);
  const [updatedPhone, updatePhone] = useState(data.contactPhone);
  const [updatedPhoneMobile, updatePhoneMobile] = useState(data.contactPhoneMobile);
  const [updatedEmail, updateEmail] = useState(data.contactEmail);
  const [emailError, setEmailError] = useState(false);
  const [telError, setTelError] = useState(false);
  const [mobilError, setMobilError] = useState(false);
  const [confirmDisabled, disableConfirm] = useState(false);

  const onConfirmClick = () => {
    const saveData = {
      updatedName,
      updatedDescription,
      updatedPhone,
      updatedPhoneMobile,
      updatedEmail,
    };
    if (confirmDisabled) return;
    onConfirm(saveData);
  };

  const onAbortClick = () => {
    onAbort();
  };
  const checkEmail = (value: string) => {
    if (!value) {
      setEmailError(false);
      return;
    }
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    setEmailError(!re.test(value.toLowerCase()));
  };
  const checkTel = (value: string) => {
    if (!value) {
      setTelError(false);
      return;
    }
    const re = /^[0-9-+\\s()]*$/i;
    setTelError(!re.test(value.toLowerCase().replace(/\s/g, '')));
  };
  const checkMobile = (value: string) => {
    if (!value) {
      setMobilError(false);
      return;
    }
    const re = /^[0-9-+\\s()]*$/i;
    setMobilError(!re.test(value.toLowerCase().replace(/\s/g, '')));
  };
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  useEffect(() => {
    if (emailError || telError || mobilError) {
      disableConfirm(true);
    } else {
      disableConfirm(false);
    }
  }, [emailError, telError, mobilError]);

  const isEditing = true;
  return (
    <>
      <div className="modal contactform-modal" role="dialog">
        <div className="modal-inner  l-container">
          <a
            role="button"
            className="modal-closer"
            onClick={onAbortClick}
            onKeyDown={(e) => {
              keyboardTriggerBasic(e, onAbortClick);
            }}
            tabIndex={0}
          >
            <span className="ic-close" />
          </a>

          <div className="modal-inner-content">
            <div className="modal-intro">
              <h2 className="hl-2">
                <Trans i18nKey="cockpit.contactModule.editModal.title">Kontakte pflegen</Trans>{' '}
              </h2>
              <p className="tx_1">
                <Trans i18nKey="cockpit.contactModule.editModal.intro">
                  Bitte tragen Sie sorgfältig die Kontaktdaten ein
                </Trans>{' '}
              </p>
            </div>

            <div className="form webforms__form">
              <div className="form-row two-cols">
                <div className="contact-info contact-name">
                  <InlineEditable
                    value={updatedName || ''}
                    placeholder={t('cockpit.contactModule.editModal.field.name', 'Vorname Name')}
                    isEditing={isEditing}
                    onChange={updateName}
                    hideWhenEmpty
                    hasError={false}
                  >
                    <h5 className="hl-5">{updatedName || ''}</h5>
                  </InlineEditable>
                </div>

                <div
                  className={`contact-info contact-description ${!updatedDescription?.trim().length ? 'is-empty' : ''}`}
                >
                  <InlineEditable
                    value={updatedDescription || ''}
                    placeholder={t('cockpit.contactModule.editModal.field.title', 'Titel oder Hinweise')}
                    isEditing={isEditing}
                    onChange={updateDescription}
                    hideWhenEmpty
                    hasError={false}
                  >
                    <p className="tx_2">{updatedDescription || ''}</p>
                  </InlineEditable>
                </div>

                <div className="contact-info contact-phone">
                  <InlineEditable
                    value={updatedPhone || ''}
                    placeholder={t('cockpit.contactModule.editModal.field.phone', 'Telefon')}
                    errorMessage={t(
                      'cockpit.contactmodule.error.tel',
                      'Bitte geben Sie die Telefonnummer im folgenden Format an: +49 69 123456789',
                    )}
                    isEditing={isEditing}
                    onChange={updatePhone}
                    onBlur={checkTel}
                    inputType="text"
                    hideWhenEmpty
                    hasError={telError}
                  >
                    <Link
                      linkHref={`tel:${updatedPhone}`}
                      linkText={updatedPhone}
                      linkClass="link multiple-link ic-phone"
                    />
                  </InlineEditable>
                </div>

                <div className="contact-info contact-phone-mobile">
                  <InlineEditable
                    value={updatedPhoneMobile || ''}
                    placeholder={t('cockpit.contactModule.editModal.field.phoneMobile', 'Mobil')}
                    errorMessage={t(
                      'cockpit.contactmodule.error.mobil',
                      'Bitte geben Sie die Mobilnummer im folgenden Format an: +49 69 123456789',
                    )}
                    isEditing={isEditing}
                    onChange={updatePhoneMobile}
                    onBlur={checkMobile}
                    inputType="text"
                    hideWhenEmpty
                    hasError={mobilError}
                  >
                    <Link
                      linkHref={`tel:${updatedPhoneMobile}`}
                      linkText={updatedPhoneMobile}
                      linkClass="link  multiple-link ic-mobile"
                    />
                  </InlineEditable>
                </div>

                <div className="contact-info contact-email">
                  <InlineEditable
                    value={updatedEmail || ''}
                    placeholder={t('cockpit.contactModule.editModal.field.email', 'E-Mail')}
                    errorMessage={t(
                      'cockpit.contactmodule.error.email',
                      'Bitte geben Sie eine gültige E-Mail-Adresse ein',
                    )}
                    isEditing={isEditing}
                    onChange={updateEmail}
                    onBlur={checkEmail}
                    inputType="text"
                    hideWhenEmpty
                    hasError={emailError}
                  >
                    <Link
                      linkHref={`mailto:${updatedEmail}`}
                      linkText={updatedEmail}
                      linkClass="link  multiple-link ic-mail"
                    />
                  </InlineEditable>
                </div>
              </div>
              <div className="modal-actions">
                <a
                  role="button"
                  className={`link--button ${confirmDisabled ? 'button--disabled' : ''}`}
                  onClick={onConfirmClick}
                  onKeyDown={(e) => {
                    keyboardTriggerBasic(e, onConfirmClick);
                  }}
                  tabIndex={0}
                  ref={modalRef}
                >
                  <span className="ic-checked" />
                  <span className="link-text">
                    <Trans i18nKey="cockpit.contactModule.editModal.confirm">{confirmText || 'Daten übernehmen'}</Trans>
                  </span>
                </a>
                <a
                  role="button"
                  className="link--button link--button-secondary"
                  onClick={onAbortClick}
                  onKeyDown={(e) => {
                    keyboardTriggerBasic(e, onAbortClick);
                  }}
                  tabIndex={0}
                >
                  <span className="ic-close" />
                  <span className="link-text">
                    <Trans i18nKey="cockpit.contactModule.editModal.abort">{abortText || 'Abbrechen'}</Trans>
                  </span>
                </a>
              </div>
              <ContactFormFooter 
                privacyLinkHref={data.privacyLinkHref as string} 
                privacyLinkText={data.privacyLinkText as string} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default ContactFormModal;
